import type { ArtMetadata } from "../schedule-presets/art-metadata";
import type { MetadataEventPayload } from "../metadata-history/metadata-events";
import type { BuilderArtwork } from "./artwork";
import type { StreamLayout } from "./day-layouts";
import { artworkPlacement } from "./artwork-placement";
import { weekdayKeys } from "../schedule-time/schedule-dates";
import { defaultElementPosition, layoutCanvasPoint, layoutLocalPoint } from "../schedule-art/layout-coordinates";
import { createStreamElement, requiredStreamBindings, streamLayoutComplete } from "./stream-layout";

export const builderDayDateFormat = "builder_day_date";

export function createOneSlotLayout(metadata: ArtMetadata, artwork: BuilderArtwork): MetadataEventPayload {
  const placements = artwork.single.map(artworkPlacement);
  const anchors = Object.fromEntries(weekdayKeys.map((day, index) => [day, placements[index]!.point])) as StreamLayout["anchors"];
  const monday = placements[0]!;
  const titleOffset = defaultElementPosition([monday.suggested ? 24 : 0, monday.textOffset[1]!]);
  const defaults = requiredStreamBindings(1).map(bind => createStreamElement(metadata, "one_slot", bind, titleOffset));
  const styles = Object.fromEntries(defaults.flatMap(entry => Object.entries(entry.styles)));
  const layout: StreamLayout = { mode: "one_slot", anchors, shared: defaults.map(entry => entry.element), exceptions: {} };
  const topElementY = Math.min(...layout.shared.map(element => element.offset[1]!));
  const dateOffset = [titleOffset[0]!, topElementY - 40];
  const dates = Object.fromEntries(weekdayKeys.map(day => {
    const original = metadata.days[day];
    const canvasPoint = layoutCanvasPoint(dateOffset, anchors[day], original.rotation);
    const offset = layoutLocalPoint(canvasPoint, original.origin, original.rotation).map(Math.round);
    return [day, { ...original.date, style: "builder_day_date_text", format: builderDayDateFormat, offset }];
  }));
  return {
    type: "day-layout/set", layout, action: "initialize", dates,
    styles: { ...styles, builder_day_date_text: { ...metadata.styles.week_date, size: 26, align: "left" } },
    formats: { [builderDayDateFormat]: "", builder_stream_time: "%H:%M" },
  };
}

export function oneSlotComplete(metadata: ArtMetadata | undefined) {
  return streamLayoutComplete(metadata, "one_slot");
}

export function dayDateEnabled(metadata: ArtMetadata) {
  return weekdayKeys.some(day => metadata.formats[metadata.days[day].date.format] !== "");
}

export function setDayDateFormat(metadata: ArtMetadata, pattern: string): MetadataEventPayload {
  const dates = Object.fromEntries(weekdayKeys.map(day => [day, { ...metadata.days[day].date, format: builderDayDateFormat }]));
  return { type: "day-dates/set", dates, styles: {}, formats: { [builderDayDateFormat]: pattern } };
}
